import * as React from "react";
import TeamsBaseComponent, {ITeamsBaseComponentState} from "msteams-react-base-component";
import {
    FilingLocation, 
    MetadataFields, 
    DmsCaseInfo,
    DmsStorageType,
} from "../../models";
import * as microsoftTeams from "@microsoft/teams-js";
import {get, post} from "../../api";
import {NetworkState} from "../ApiCallState";
import {TeamsGetRecentDocumentsRequest} from "../../services/teams/TeamsGetRecentDocumentsRequest";
import {TeamsApiService} from "../../services/teams/TeamsApiService";

export interface IDocumentsState extends ITeamsBaseComponentState {
    documents: FilingLocation[];
    documentsErrorMessage: string | null;
    metadataFields: MetadataFields[];
    metadataOrder: string[];
    originalMetadataOrder: string[];
    metadataEnabled: boolean;
    rootNetworkState: NetworkState;
    metadataNetworkState: NetworkState;
    isTeamOwner: boolean;
    teamsId: string;
    tenantId: string;
    channelId: string;
    userId: string;
    isPrivateChannel: boolean;
    litigationHoldNetworkState: NetworkState;
    frictionlesSpExperienceNetworkState: NetworkState;
}

export const TeamsDocumentsContext: React.Context<TeamsDocumentsContextProps> = React.createContext({
    theme: null,
    documents: [],
    metadataFields: [],
    metadataOrder: [],
    originalMetadataOrder: [],
    documentsErrorMessage: null,
    metadataEnabled: false,
    rootNetworkState: NetworkState.Idle,
    metadataNetworkState: NetworkState.Idle,
    isTeamOwner: false,
    teamsId: "",
    tenantId: "",
    channelId: "",
    userId: "",
    setParentState: () => {},
    refreshTeamsChannelDocuments: () => Promise.resolve(),
    fetchRoot: () => Promise.resolve(),
    changeTeamsChannel: () => Promise.resolve(),
    isPrivateChannel: false,
    litigationHoldNetworkState: NetworkState.Idle,
    frictionlesSpExperienceNetworkState: NetworkState.Idle
});

export interface TeamsDocumentsContextProps extends IDocumentsState {
    setParentState: (state: Partial<IDocumentsState>) => void;
    refreshTeamsChannelDocuments: () => Promise<void>;
    changeTeamsChannel: (channelId: string, data: {icon: string}) => Promise<void>;
    fetchRoot: () => Promise<void>;
}

export interface IDocumentsProps {
    caseInfo: DmsCaseInfo;
    setLitigationHoldValue: (value: boolean) => void;
    setLitigationHoldError: (value: string) => void;
    setFrictionlessExperienceValue: (value: boolean) => void;
    children: React.ReactElement;
}

export class TeamsDocuments extends TeamsBaseComponent<IDocumentsProps, IDocumentsState> {
    teamsApiService = new TeamsApiService();

    constructor(props: IDocumentsProps) {
        super(props);
        this.state = {
            theme: null,
            documents: [],
            documentsErrorMessage: null,
            metadataFields: [],
            metadataOrder: [],
            originalMetadataOrder: [],
            metadataEnabled: this.props.caseInfo.StorageType == "netdocuments",
            rootNetworkState: NetworkState.Idle,
            metadataNetworkState: NetworkState.Idle,
            isTeamOwner: false,
            teamsId: this.getQueryVariable("teamsId"),
            channelId: this.getQueryVariable("channelId"),
            userId: this.getQueryVariable("userId"),
            tenantId: this.getQueryVariable("tenantId"),
            isPrivateChannel: false,
            litigationHoldNetworkState: NetworkState.Idle,
            frictionlesSpExperienceNetworkState: NetworkState.Idle
        };
    }

    public async componentWillMount(): Promise<void> {
        microsoftTeams.initialize();
        this.FetchRoot();
    }

    public FetchRoot = async () => {
        if (this.state.rootNetworkState == NetworkState.Loading) return;
        if (this.state.rootNetworkState != NetworkState.Success)
            this.setState({rootNetworkState: NetworkState.Loading});
        await Promise.all([this.refreshTeamsChannelDocuments(), this.isLitigationHoldEnabled(), this.isFrictionlessSpExperienceEnabled(), this.getMetadataFields(), this.getTeamOwners()]);
    };

    private async refreshTeamsChannelDocuments() {
        const request: TeamsGetRecentDocumentsRequest = {
            channelId: this.state.channelId,
            recentDocs: false,
            storageType: this.props.caseInfo.StorageType,
            teamId: this.state.teamsId,
            tenantId: this.state.tenantId,
            userId: this.state.userId,
        };

        await this.teamsApiService.getTeamsRecentDocuments(
            request,
            "Teams document retrieval failed.",
            (response) => {
                const docs = response as FilingLocation[];
                this.setState({documents: docs, documentsErrorMessage: null, rootNetworkState: NetworkState.Success});
            },
            (error) => this.setState({documentsErrorMessage: error, rootNetworkState: NetworkState.Error})
        );
    }

    private async changeTeamsChannel(channelId: string, data: {icon: string}) {
        const privateChannel = data && data.icon == "Lock";

        this.setState({rootNetworkState: NetworkState.Loading, channelId: channelId, isPrivateChannel: privateChannel});
        const request: TeamsGetRecentDocumentsRequest = {
            channelId: channelId,
            recentDocs: false,
            storageType: this.props.caseInfo.StorageType,
            teamId: this.state.teamsId,
            tenantId: this.state.tenantId,
            userId: this.state.userId,
        };

        await this.teamsApiService.getTeamsRecentDocuments(
            request,
            "Teams document retrieval failed.",
            (response) => {
                const docs = response as FilingLocation[];
                this.setState({documents: docs, documentsErrorMessage: null, rootNetworkState: NetworkState.Success});
            },
            (error) => this.setState({documentsErrorMessage: error, rootNetworkState: NetworkState.Error})
        );
    }

    getTeamOwners = async () => {
        if (!this.state.metadataEnabled) return;
        await get(
            "/api/teamsdmsintegration/IsUserTeamOwnerMetadata?teamsId=" +
                this.state.teamsId +
                "&tenantId=" +
                this.state.tenantId
        )
            .then((r) => {
                if (r.status.toString().startsWith("2")) {
                    return r.json();
                }
            })
            .then((response) => {
                const teamOwner = response as boolean;

                this.setState({isTeamOwner: teamOwner});
            });
    };

    async isLitigationHoldEnabled() {
        if(this.props.caseInfo.StorageType !== DmsStorageType.NetDocuments) {
            this.setState({litigationHoldNetworkState: NetworkState.Success});
            return;
        }
        if (this.state.litigationHoldNetworkState == NetworkState.Idle){
            this.props.setLitigationHoldValue(true);
            this.setState({litigationHoldNetworkState: NetworkState.Loading});
        }

        await post("/api/teamsdmsintegration/IsLitigationHoldEnabled", {
            TenantId: this.state.tenantId,
            TeamsId: this.state.teamsId,
            CaseDetails: this.props.caseInfo.CaseDetails,
            StorageType: this.props.caseInfo.StorageType,
        })
        .then((res) => res.json())
        .then((response) => {

            if(response.Error === "MissingWorkspacePropertiesException"){
                this.props.setLitigationHoldValue(false);
                this.props.setLitigationHoldError("Unable to determine if litigation hold value is enabled. Please ensure the Client and Matter IDs are configured for this workspace.");
                this.setState({litigationHoldNetworkState: NetworkState.Error});
                return;
            }
            else if(response.Error === "GenericLitigationHoldException"){
                this.props.setLitigationHoldValue(false);
                this.props.setLitigationHoldError("Unable to determine if litigation hold value is enabled.");
                this.setState({litigationHoldNetworkState: NetworkState.Error});
                return;
            }

            this.props.setLitigationHoldValue(response.IsEnabled);
            this.setState({litigationHoldNetworkState: NetworkState.Success});
        })
        .catch(() => {
            this.setState({litigationHoldNetworkState: NetworkState.Error})});
    }

    isFrictionlessSpExperienceEnabled(){
        if(this.props.caseInfo.StorageType !== DmsStorageType.SharePoint) {
            this.setState({frictionlesSpExperienceNetworkState: NetworkState.Success});
            return;
        }

        if (this.state.frictionlesSpExperienceNetworkState == NetworkState.Idle){
            this.props.setFrictionlessExperienceValue(false);
            this.setState({frictionlesSpExperienceNetworkState: NetworkState.Loading});
        }
        
        const caseDetails = JSON.parse(this.props.caseInfo.CaseDetails);

        post("/api/teamsdmsintegration/IsFrictionlessSpExperienceEnabled", {
            TeamsId: this.state.teamsId,
            TenantId: this.state.tenantId,
            SpHostKVPrefix: caseDetails.SpHostKVPrefix,
        })
        .then((response) => response.json())
        .then((response) => {
            this.props.setFrictionlessExperienceValue(response);
            this.setState({frictionlesSpExperienceNetworkState: NetworkState.Success});
        })
        .catch(() => this.setState({frictionlesSpExperienceNetworkState: NetworkState.Error}));
    }
 
    async getMetadataFields() {
        if (!this.state.metadataEnabled) {
            this.setState({metadataNetworkState: NetworkState.Success});
            return;
        }
        if (this.state.metadataNetworkState == NetworkState.Idle)
            this.setState({metadataNetworkState: NetworkState.Loading});
        await post("/api/teamsdmsintegration/GetMetadataFields", {
            TeamsId: this.state.teamsId,
            TenantId: this.state.tenantId,
            CaseDetails: this.props.caseInfo.CaseDetails,
            StorageType: this.props.caseInfo.StorageType,
        })
            .then((response) => response.json())
            .then((response) => {
                const metadataFields = response as MetadataFields[];

                const metadataOrder: string[] = [];

                for (const option of metadataFields) {
                    const item = option.Id;
                    metadataOrder.push(item);
                }

                let metadataEnabled = this.state.metadataEnabled;

                if (metadataFields.length == 0) {
                    metadataEnabled = false;
                }

                this.setState({
                    metadataFields: metadataFields,
                    metadataOrder: metadataOrder,
                    metadataEnabled: metadataEnabled,
                });
            });

        await post("/api/teamsdmsintegration/GetMetadataOrder", {
            TeamsId: this.state.teamsId,
            TenantId: this.state.tenantId,
            CaseDetails: this.props.caseInfo.CaseDetails,
            StorageType: this.props.caseInfo.StorageType,
        })
            .then((response) => response.json())
            .then((response) => {
                const metadataOrder = response as string[]; // list of ids in the order we want to display

                if (metadataOrder.length != 0) {
                    const metadataFields = this.state.metadataFields; // list of profile attributes enabled for the cabinet

                    metadataFields.sort((a, b) => metadataOrder.indexOf(a.Id) - metadataOrder.indexOf(b.Id));

                    const newFields = metadataFields.filter((i) => metadataOrder.indexOf(i.Id) == -1);

                    for (const field of newFields) {
                        const indexOfNewField = metadataFields.indexOf(field);
                        metadataFields.push(metadataFields.splice(indexOfNewField, 1)[0]);
                    }

                    this.setState({metadataFields: metadataFields, metadataOrder: metadataOrder});
                }
            });

        const originalMetadataOrder = {...this.state.metadataOrder}; // creates a clone of the object without a reference. Without this, originalMetadataOrder gets changed when metadataOrder does

        this.setState({originalMetadataOrder: originalMetadataOrder, metadataNetworkState: NetworkState.Success});
    }

    public render() {
        return (
            <div>
                <TeamsDocumentsContext.Provider
                    value={{
                        ...this.state,
                        setParentState: (state: Pick<IDocumentsState, keyof IDocumentsState>) => this.setState(state),
                        refreshTeamsChannelDocuments: () => this.refreshTeamsChannelDocuments(),
                        changeTeamsChannel: (channelId: string, data: {icon: string}) =>
                            this.changeTeamsChannel(channelId, data),
                        fetchRoot: () => this.FetchRoot(),
                    }}
                >
                    {this.props.children}
                </TeamsDocumentsContext.Provider>
            </div>
        );
    }
}

TeamsDocuments.contextType = TeamsDocumentsContext;
